import { useSharingAnalytics } from '@/components/modal/share-session-modal/hooks/use-sharing-analytics';
import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Tracks share_notification_received (once on render) and provides
 * a handler for share_notification_clicked.
 *
 * Uses useSharingAnalytics as the single dispatch layer so all sharing
 * analytics go through one place.
 */
export function useShareNotificationTracking(
  isSessionShared: boolean,
  notificationId: string | undefined,
  sessionId: string | undefined
) {
  const { trackShareNotificationReceived, trackShareNotificationClicked } =
    useSharingAnalytics();

  const hasTrackedReceived = useRef(false);
  useEffect(() => {
    if (isSessionShared && notificationId && !hasTrackedReceived.current) {
      hasTrackedReceived.current = true;
      trackShareNotificationReceived({
        notification_id: notificationId,
        session_id: sessionId,
      });
    }
  }, [isSessionShared, notificationId, sessionId, trackShareNotificationReceived]);

  const trackClicked = useCallback(() => {
    if (isSessionShared && notificationId) {
      trackShareNotificationClicked({
        notification_id: notificationId,
        session_id: sessionId,
      });
    }
  }, [isSessionShared, notificationId, sessionId, trackShareNotificationClicked]);

  return useMemo(() => ({ trackClicked }), [trackClicked]);
}
